import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateQuantity, removeFromCart } from '../../../features/cart/cartSlice';
import { useCustomerAuth } from '../../../contexts/CustomerAuthContext';
import { apiFetch } from '../../../utils/api';
import { getStorefrontLineItemAvailability } from '../../../utils/storefrontVariantUtils';
import { STEPS } from '../CheckoutModal';

const CartReviewStep = ({
  state,
  dispatch: modalDispatch,
  storeData,
  storeSlug,
  formatPrice,
  goToStep,
  cartItems,
  cartTotal,
  cartCount,
  onClose
}) => {
  const reduxDispatch = useDispatch();
  const { isAuthenticated, customer } = useCustomerAuth();
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [shareFlyoutId, setShareFlyoutId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const flyoutRef = useRef(null);

  // Close share flyout when clicking outside
  useEffect(() => {
    if (!shareFlyoutId) return;
    const handleClickOutside = (e) => {
      if (flyoutRef.current && !flyoutRef.current.contains(e.target)) {
        setShareFlyoutId(null);
        setCopySuccess(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [shareFlyoutId]);

  // -- Quantity controls --
  const handleIncrement = (item) => {
    const availability = getStorefrontLineItemAvailability(item.product, item);
    const maxStock = availability?.availableQuantity ?? item.product?.stock ?? item.product?.quantity ?? 99;
    if (item.qty >= maxStock) return;
    const productId = item.product.id || item.product._id;
    const variantId = item.variantId || item.product?.variantId || null;
    reduxDispatch(updateQuantity({
      productId,
      variantId,
      quantity: item.qty + 1,
      variations: item.variations
    }));
  };

  const handleDecrement = (item) => {
    const productId = item.product.id || item.product._id;
    const variantId = item.variantId || item.product?.variantId || null;
    if (item.qty <= 1) return;
    reduxDispatch(updateQuantity({
      productId,
      variantId,
      quantity: item.qty - 1,
      variations: item.variations
    }));
  };

  const handleRemove = (item) => {
    const productId = item.product.id || item.product._id;
    const variantId = item.variantId || item.product?.variantId || null;
    reduxDispatch(removeFromCart({
      productId,
      variantId,
      variations: item.variations
    }));
  };

  // -- Share flyout --
  const handleShareToggle = (productId) => {
    if (shareFlyoutId === productId) {
      setShareFlyoutId(null);
      setCopySuccess(false);
    } else {
      setShareFlyoutId(productId);
      setCopySuccess(false);
    }
  };

  const handleCopyLink = async (product) => {
    const productUrl = `${window.location.origin}/products/${product.slug}`;
    try {
      await navigator.clipboard.writeText(productUrl);
      setCopySuccess(true);
      setTimeout(() => {
        setShareFlyoutId(null);
        setCopySuccess(false);
      }, 1500);
    } catch (err) {
      // fallback: select input text
    }
  };

  // -- Coupon --
  const handleApplyCoupon = async () => {
    const code = state.couponCode?.trim().toUpperCase();
    if (!code) return;

    setCouponLoading(true);
    setCouponError('');

    try {
      const merchantId = storeData?.ownerId || storeData?.owner?.id;
      const resp = await apiFetch('/discounts/validate', {
        method: 'POST',
        body: JSON.stringify({
          code,
          merchantId,
          storeSlug,
          orderData: { subtotal: cartTotal }
        })
      });

      const data = await resp.json();

      if (resp.ok && data.valid) {
        // Store discount with discountAmount from response
        modalDispatch({
          type: 'SET_FIELDS',
          fields: {
            appliedDiscount: {
              ...data.discount,
              discountAmount: data.discountAmount || 0 // Backend calculates this correctly
            },
            couponCode: code
          }
        });
        setCouponError('');
      } else {
        setCouponError(data.error || data.message || 'Invalid coupon code');
        modalDispatch({
          type: 'SET_FIELDS',
          fields: { appliedDiscount: null }
        });
      }
    } catch (err) {
      setCouponError('Failed to validate coupon. Please try again.');
      modalDispatch({
        type: 'SET_FIELDS',
        fields: { appliedDiscount: null }
      });
    } finally {
      setCouponLoading(false);
    }
  };

  // -- Navigation --
  const handleProceedToCheckout = () => {
    // If auth is required, check if already authenticated
    if (storeData.requireAccountRegistration) {
      if (isAuthenticated && customer) {
        // Already authenticated - pre-fill customer data and go to PURCHASE_INFO
        modalDispatch({
          type: 'SET_FIELDS',
          payload: {
            email: customer.email || '',
            fullName: [customer.firstName, customer.lastName].filter(Boolean).join(' '),
            whatsappNumber: customer.phone || '',
            customerId: customer._id || customer.id || null
          }
        });
        goToStep(STEPS.PURCHASE_INFO);
      } else {
        // Not authenticated - go to AUTH step
        goToStep(STEPS.AUTH);
      }
    } else {
      // Auth not required - proceed to guest checkout
      goToStep(STEPS.VISITOR_INFO);
    }
  };

  const handleMakeEnquiry = () => {
    goToStep(STEPS.ENQUIRY_INFO);
  };

  // -- Computed values --
  const discount = state.appliedDiscount;
  // Use pre-calculated discountAmount from backend if available, otherwise calculate locally
  const discountAmount = discount
    ? discount.discountAmount != null
      ? discount.discountAmount
      : discount.type?.toUpperCase() === 'PERCENTAGE'
        ? (cartTotal * discount.value) / 100
        : discount.value
    : 0;
  const finalTotal = cartTotal - discountAmount;

  const showWhatsApp = storeData?.whatsappOrdersEnabled && storeData?.whatsappNumber;

  // -- Variation label --
  const formatVariations = (variations) => {
    if (!variations || Object.keys(variations).length === 0) return null;
    return Object.entries(variations)
      .map(([key, value]) => `${key}: ${value}`)
      .join(' \u00B7 ');
  };

  // -- Empty cart --
  if (!cartItems || cartItems.length === 0) {
    return (
      <div className="cm-cart-empty">
        <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
        </svg>
        <p className="cm-cart-empty-text">Your cart is empty</p>
        <button className="cm-btn-primary" onClick={onClose} style={{ maxWidth: 200 }}>
          Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="cm-step-content">
      {/* Item list */}
      <div className="cm-cart-items">
        {cartItems.map((item, index) => {
          const product = item.product;
          const productId = product.id || product._id;
          const imageUrl = product.primaryImage || product.images?.[0];
          const variationLabel = formatVariations(item.variations);
          const itemKey = item.variantSku || `${productId}-${index}`;
          const isShareOpen = shareFlyoutId === productId;
          const productUrl = `${window.location.origin}/products/${product.slug}`;

          return (
            <div key={itemKey} className="cm-cart-item">
              {/* Product image */}
              <div className="cm-cart-item-image">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={product.name}
                    className="cm-cart-item-img"
                  />
                ) : (
                  <div className="cm-cart-item-placeholder">
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Product details */}
              <div className="cm-cart-item-details">
                <p className="cm-cart-item-name">{product.name}</p>
                {variationLabel && (
                  <p className="cm-cart-item-variation">{variationLabel}</p>
                )}
                <p className="cm-cart-item-price">{formatPrice(product.price)}</p>

                {/* Quantity controls */}
                <div className="cm-cart-qty">
                  <button
                    className="cm-cart-qty-btn"
                    onClick={() => handleDecrement(item)}
                    disabled={item.qty <= 1}
                    aria-label="Decrease quantity"
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                  <span className="cm-cart-qty-value">{item.qty}</span>
                  <button
                    className="cm-cart-qty-btn"
                    onClick={() => handleIncrement(item)}
                    disabled={item.qty >= (getStorefrontLineItemAvailability(item.product, item)?.availableQuantity ?? item.product?.stock ?? item.product?.quantity ?? 99)}
                    aria-label="Increase quantity"
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Action icons */}
              <div className="cm-cart-item-actions">
                {/* Share icon - modern external link */}
                <div className="cm-share-wrapper" ref={isShareOpen ? flyoutRef : null}>
                  <button
                    className="cm-cart-icon-btn"
                    onClick={() => handleShareToggle(productId)}
                    aria-label="Share product"
                    title="Share"
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                    </svg>
                  </button>
                  {isShareOpen && (
                    <div className="cm-share-flyout">
                      <input
                        className="cm-share-flyout-input"
                        type="text"
                        value={productUrl}
                        readOnly
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        className={`cm-share-flyout-btn${copySuccess ? ' copied' : ''}`}
                        onClick={() => handleCopyLink(product)}
                      >
                        {copySuccess ? (
                          <>
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Copied
                          </>
                        ) : (
                          <>
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <rect x="9" y="9" width="13" height="13" rx="2" />
                              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Delete icon - modern trash can */}
                <button
                  className="cm-cart-icon-btn cm-cart-icon-delete"
                  onClick={() => handleRemove(item)}
                  aria-label="Remove item"
                  title="Remove"
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18" />
                    <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Coupon section */}
      <div className="cm-coupon-section">
        <div className="cm-coupon-row">
          <input
            type="text"
            placeholder="Coupon code"
            value={state.couponCode || ''}
            onChange={(e) =>
              modalDispatch({ type: 'UPDATE_FIELD', field: 'couponCode', value: e.target.value })
            }
            className="cm-coupon-input"
          />
          <button
            className="cm-coupon-btn"
            onClick={handleApplyCoupon}
            disabled={couponLoading || !state.couponCode?.trim()}
          >
            {couponLoading ? 'Applying...' : 'Apply'}
          </button>
        </div>
        {couponError && <p className="cm-coupon-error">{couponError}</p>}
        {discount && (
          <p className="cm-coupon-success">
            Discount applied: {discount.type?.toUpperCase() === 'PERCENTAGE' ? `${discount.value}%` : formatPrice(discount.value)} off
          </p>
        )}
      </div>

      {/* Cart summary */}
      <div className="cm-cart-summary">
        <div className="cm-cart-summary-row">
          <span className="cm-cart-summary-label">Subtotal</span>
          <span className="cm-cart-summary-value">{formatPrice(cartTotal)}</span>
        </div>
        {discount && (
          <>
            <div className="cm-cart-summary-row">
              <span className="cm-cart-summary-label">Discount</span>
              <span className="cm-cart-summary-value discount">
                -{formatPrice(discountAmount)}
              </span>
            </div>
            <div className="cm-cart-summary-row cm-cart-summary-total">
              <span className="cm-cart-summary-label">Total</span>
              <span className="cm-cart-summary-value">{formatPrice(finalTotal)}</span>
            </div>
          </>
        )}
      </div>

      {/* Action buttons */}
      <div className="cm-cart-actions">
        {showWhatsApp ? (
          <>
            <button className="cm-enquiry-btn" onClick={handleMakeEnquiry}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span>Make an Enquiry</span>
            </button>
            <button className="cm-checkout-btn" onClick={handleProceedToCheckout}>
              Proceed to Checkout
            </button>
          </>
        ) : (
          <button
            className="cm-checkout-btn"
            onClick={handleProceedToCheckout}
            style={{ width: '100%' }}
          >
            Proceed to Checkout
          </button>
        )}
      </div>
    </div>
  );
};

export default CartReviewStep;
